import { FormikProvider, useFormik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import Button from "src/components/buttons/Button";
import Input from "src/components/inputs/Input";
import NestedNavigationByProps from "src/components/nested-navigation/NestedNavigationByProps";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import { number, object } from "yup";
import { useCreateTaxes, useGetTaxes } from "../api-clients";

const tabs = [
  {
    label: "Service",
    value: 1,
  },
  {
    label: "Package",
    value: 2,
  },
  {
    label: "Course",
    value: 3,
  },
];

const FORM_FIELDS = {
  SERVICE_TYPE: "serviceType",
  TAX: "tax",
  GST: "gst",
};

const EditSettings = ({ isInViewMode = false }: { isInViewMode?: boolean }) => {
  useSetActiveTab(TAB_NAMES.EDIT_STORE_SETTINGS);

  const [activeTab, setActiveTab] = useState(tabs[0].value);

  const { isLoading: isGetTaxesLoading, data, mutate } = useGetTaxes();

  const taxesData = useMemo(() => data?.data ?? [], [data?.data]);

  const taxDataByTab = useMemo(
    () =>
      taxesData?.find(
        ({ serviceType }: { serviceType: number }) => activeTab === serviceType
      ) ?? {},
    [activeTab, taxesData]
  );

  return (
    <div className="flex flex-col gap-[20px] h-full">
      <TabHeader label="Settings" subLabel="All System Settings" />

      <div className="flex flex-col justify-between gap-6 bg-white rounded-md py-[30px] px-[42px] h-fit">
        <NestedNavigationByProps
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={(activeTab) => [setActiveTab(activeTab)]}
        />
        <TaxForm
          activeTab={activeTab}
          data={taxDataByTab}
          successOnSubmit={mutate}
        />
      </div>
    </div>
  );
};

export default EditSettings;

const TaxForm = ({
  activeTab,
  data,
  successOnSubmit,
}: {
  activeTab: number;
  data: { tax?: number; gst?: number };
  successOnSubmit: () => void;
}) => {
  const { errorNotification, successNotification } = useNotification();

  const {
    isLoading: isSaveTaxesLoading,
    isSuccess,
    execute,
    error,
  } = useCreateTaxes();

  useEffect(() => {
    if (isSuccess) {
      successNotification("Taxes Saved.");
      successOnSubmit();
    } else if (error) {
      errorNotification("Something went wrong");
    }
  }, [
    error,
    errorNotification,
    isSuccess,
    successNotification,
    successOnSubmit,
  ]);

  const isInLoading = useMemo(() => isSaveTaxesLoading, [isSaveTaxesLoading]);

  const isFieldDisabled = useMemo(() => isInLoading, [isInLoading]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      [FORM_FIELDS.TAX]: data?.tax ?? "",
      [FORM_FIELDS.GST]: data?.gst ?? "",
    },
    validationSchema: object({
      [FORM_FIELDS.TAX]: number().required("Required"),
      [FORM_FIELDS.GST]: number().required("Required"),
    }),
    onSubmit: (data) => {
      data.serviceType = activeTab;
      execute({ data });
    },
  });

  const { values, errors, handleSubmit, setFieldValue, touched, resetForm } =
    formik;

  const getFieldError = useCallback(
    (fieldName: string) => {
      return touched[fieldName] ? (errors[fieldName] as string) : undefined;
    },
    [errors, touched]
  );

  return (
    <FormikProvider value={formik}>
      <div className="flex flex-col justify-between gap-6 bg-white rounded-md h-fit">
        <div className="grid grid-cols-2 gap-8">
          <Input
            type="number"
            label="Tax"
            placeholder=""
            name={FORM_FIELDS.TAX}
            value={values[FORM_FIELDS.TAX] as string}
            error={getFieldError(FORM_FIELDS.TAX)}
            onChange={({ target: { value } }) =>
              setFieldValue(FORM_FIELDS.TAX, value ? Number(value) : "")
            }
            rightIcon={RightIcon}
            disabled={isFieldDisabled}
            required
          />
          <Input
            type="number"
            label="GST"
            placeholder=""
            name={FORM_FIELDS.GST}
            value={values[FORM_FIELDS.GST] as string}
            error={getFieldError(FORM_FIELDS.GST)}
            onChange={({ target: { value } }) =>
              setFieldValue(FORM_FIELDS.GST, value ? Number(value) : "")
            }
            rightIcon={RightIcon}
            disabled={isFieldDisabled}
            required
          />

          <div className="flex gap-4">
            <Button
              text={"Reset"}
              onClick={resetForm}
              className="h-[40px] px-14"
              isDisabled={isFieldDisabled}
              primary
              ghost
            />
            <Button
              text={isInLoading ? "Loading..." : "Submit"}
              onClick={handleSubmit}
              primary
              className="h-[40px] px-14"
              isDisabled={isFieldDisabled}
            />
          </div>
        </div>
      </div>
    </FormikProvider>
  );
};

const RightIcon = () => <span className="text-gray-suva border-l pl-2">%</span>;
